import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FAMILY_ARCHIVE } from "../data/site";

export default function FamilyArchive({ compact = false }) {
  const [threeJoes, jerryAndJoe] = FAMILY_ARCHIVE.photos;

  if (compact) {
    return (
      <aside
        className="border border-border bg-[hsl(var(--surface))] p-5 md:p-6 grid grid-cols-[96px_1fr] md:grid-cols-[140px_1fr] gap-5 md:gap-6 items-center"
        data-testid="family-archive-compact"
      >
        <div className="aspect-[4/5] overflow-hidden bg-background">
          <img src={threeJoes.src} alt={threeJoes.alt} className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div>
          <p className="label mb-2">Where my work started</p>
          <p className="serif italic text-lg md:text-xl leading-snug">
            Three generations of Graves cowboys — my grandfather, my father, and me at nine.
          </p>
          <p className="mt-2 text-sm text-[hsl(var(--ink-soft))] leading-relaxed">
            Family legacy is where the Black Western series began. If your piece is about
            your people, you're in the right place.
          </p>
          <Link to="/about#family" className="mt-3 inline-flex items-center gap-2 label link-underline" data-testid="family-archive-compact-link">
            The family story <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </aside>
    );
  }

  return (
    <section id="family" className="border-t border-border scroll-mt-28" data-testid="family-archive">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <header className="mb-12 md:mb-16 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-7">
            <p className="label mb-3">{FAMILY_ARCHIVE.eyebrow}</p>
            <h2 className="serif text-4xl md:text-5xl lg:text-6xl tracking-tight font-light leading-[1.02]">
              {FAMILY_ARCHIVE.title}{" "}
              <span className="italic text-[hsl(var(--ink-soft))]">{FAMILY_ARCHIVE.titleItalic}</span>
            </h2>
          </div>
          <p className="md:col-span-5 text-[hsl(var(--ink-soft))] leading-relaxed">{FAMILY_ARCHIVE.intro}</p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <figure className="lg:col-span-7" data-testid="family-photo-three-joes">
            <div className="aspect-[4/5] overflow-hidden bg-[hsl(var(--surface))]">
              <img src={threeJoes.src} alt={threeJoes.alt} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <figcaption className="mt-4">
              <p className="serif italic text-xl md:text-2xl">{threeJoes.caption}</p>
              <p className="label mt-1 text-[hsl(var(--ink-soft))]">{threeJoes.meta}</p>
            </figcaption>
          </figure>

          <div className="lg:col-span-5 space-y-10">
            <figure data-testid="family-photo-jerry-joe">
              <div className="aspect-[4/5] overflow-hidden bg-[hsl(var(--surface))]">
                <img src={jerryAndJoe.src} alt={jerryAndJoe.alt} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <figcaption className="mt-4">
                <p className="serif italic text-xl md:text-2xl">{jerryAndJoe.caption}</p>
                <p className="label mt-1 text-[hsl(var(--ink-soft))]">{jerryAndJoe.meta}</p>
              </figcaption>
            </figure>

            <div className="border-t border-border pt-8" data-testid="family-grandfather">
              <p className="label mb-3">Charles "Joe" Graves · b. December 23, 1911</p>
              <ul className="space-y-2">
                {FAMILY_ARCHIVE.grandfather.map((line) => (
                  <li key={line} className="serif text-lg md:text-xl leading-snug">{line}</li>
                ))}
              </ul>
              <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
                <Link to={`/work/${FAMILY_ARCHIVE.portrait.slug}/${FAMILY_ARCHIVE.portrait.id}`} className="inline-flex items-center gap-2 label link-underline" data-testid="family-portrait-link">
                  See the portrait · {FAMILY_ARCHIVE.portrait.title} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <Link to="/journal/three-hats" className="inline-flex items-center gap-2 label link-underline" data-testid="family-journal-link">
                  Read "Three hats" <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        <p className="mt-10 text-xs text-[hsl(var(--ink-soft))]">{FAMILY_ARCHIVE.notice}</p>
      </div>
    </section>
  );
}
